import { useState, useEffect, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { estimatesService } from '@/services/estimates-service'
import { clientsService } from '@/services/clients-service'
import { companyService } from '@/services/company-service'
import { AddressAutocomplete } from '@/components/shared/address-autocomplete'
import { diffClientAddress, EMPTY_CLIENT_ADDRESS, type ClientAddress } from '@/lib/client-address'
import type { Client, CreateEstimatePayload } from '@/types'

/* ── Phone number auto-format (xxx-xxx-xxxx) ── */
function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10)
  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
}

/* ── Inline SVG icons for project type pills ── */
const AsphaltIcon = () => (
  <svg className="wz-pill-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 20h16" /><path d="M6 16h12" /><path d="M8 12h8" /><rect x="3" y="4" width="18" height="4" rx="1" />
  </svg>
)
const ConcreteIcon = () => (
  <svg className="wz-pill-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
)
const GradingIcon = () => (
  <svg className="wz-pill-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 20l7-7 3 3 10-10" /><path d="M17 6h5v5" />
  </svg>
)
const SealCoatIcon = () => (
  <svg className="wz-pill-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" /><path d="M12 3v18" /><path d="M3 12h18" />
  </svg>
)
const StripingIcon = () => (
  <svg className="wz-pill-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 6h16" /><path d="M4 12h16" /><path d="M4 18h16" />
  </svg>
)

const PROJECT_TYPES = [
  { id: 'asphalt', label: 'Asphalt', Icon: AsphaltIcon },
  { id: 'concrete', label: 'Concrete', Icon: ConcreteIcon },
  { id: 'grading', label: 'Grading', Icon: GradingIcon },
  { id: 'seal-coat', label: 'Seal Coat', Icon: SealCoatIcon },
  { id: 'striping', label: 'Striping', Icon: StripingIcon },
]

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY',
]

const TOTAL_STEPS = 4

interface NewEstimateWizardProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Prefill customer name from RFP (first + last name) */
  prefillClientName?: string
  /** Prefill address from RFP (full "street, city, state zip" string) */
  prefillAddress?: string
  /** Prefill email from RFP */
  prefillEmail?: string
  /** Prefill phone from RFP */
  prefillPhone?: string
  /** Prefill scope of work from RFP */
  prefillScopeOfWork?: string
}

export function NewEstimateWizard({ open, onOpenChange, prefillClientName, prefillAddress, prefillEmail, prefillPhone, prefillScopeOfWork }: NewEstimateWizardProps) {
  const navigate = useNavigate()
  const [step, setStep] = useState(0) // 0-indexed for translateX math
  const [direction, setDirection] = useState<'forward' | 'back'>('forward')
  const [isAnimating, setIsAnimating] = useState(false)
  const trackRef = useRef<HTMLDivElement>(null)

  // Step 1
  const [isPublicWorks, setIsPublicWorks] = useState(false)

  // Step 2
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set())

  // Step 3
  const [clients, setClients] = useState<Client[]>([])
  const [selectedClientId, setSelectedClientId] = useState<string>('')
  const [clientSearch, setClientSearch] = useState('')
  const [showClientDropdown, setShowClientDropdown] = useState(false)
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null)
  const clientComboRef = useRef<HTMLDivElement>(null)
  const clientInputWrapRef = useRef<HTMLDivElement>(null)
  const newClientFormRef = useRef<HTMLDivElement>(null)
  const [showNewClientForm, setShowNewClientForm] = useState(false)
  const [isCreatingClient, setIsCreatingClient] = useState(false)
  const [newClient, setNewClient] = useState({
    firstName: '', lastName: '', email: '', phone: '', company: '',
    isResidence: false, address: '', city: '', state: '', zip: '',
  })
  const [clientError, setClientError] = useState('')

  // Address of an ALREADY-SELECTED client, editable in place. Kept separate from
  // `newClient` because it edits the shared Client record rather than creating
  // one. `loadedClientAddressRef` is the server's value, so advancing the step
  // can PUT only when something actually changed.
  const [clientAddress, setClientAddress] = useState<ClientAddress>(EMPTY_CLIENT_ADDRESS)
  const loadedClientAddressRef = useRef<ClientAddress>(EMPTY_CLIENT_ADDRESS)
  const [addressLoadedFor, setAddressLoadedFor] = useState('')
  const [isSavingClientAddress, setIsSavingClientAddress] = useState(false)

  // Company default markup (fetched from settings)
  const [companyMarkup, setCompanyMarkup] = useState<number | null>(null)

  // Step 4
  const [isUnitPricing, setIsUnitPricing] = useState(false)
  const [projectName, setProjectName] = useState('')
  const [jobAddress, setJobAddress] = useState('')
  const [jobCity, setJobCity] = useState('')
  const [jobState, setJobState] = useState('')
  const [jobZip, setJobZip] = useState('')
  const [supervisorName, setSupervisorName] = useState('')
  const [supervisorPhone, setSupervisorPhone] = useState('')
  const [supervisorEmail, setSupervisorEmail] = useState('')

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')

  // Load clients on open
  useEffect(() => {
    if (open) {
      loadClients()
      loadCompanyMarkup()
      setStep(0)
      setDirection('forward')
      setIsPublicWorks(false)
      setSelectedTypes(new Set())
      setSelectedClientId('')
      setClientSearch('')
      setShowClientDropdown(false)
      setClientError('')
      setIsUnitPricing(false)
      setProjectName('')
      setJobAddress('')
      setJobCity('')
      setJobState('')
      setJobZip('')
      setSupervisorName('')
      setSupervisorPhone('')
      setSupervisorEmail('')
      setSubmitError('')

      // Prefill from RFP if provided
      if (prefillClientName || prefillAddress || prefillEmail || prefillPhone) {
        setShowNewClientForm(true)
        const clientData = { firstName: '', lastName: '', email: '', phone: '', company: '', isResidence: false, address: '', city: '', state: '', zip: '' }
        if (prefillClientName) {
          const nameParts = prefillClientName.trim().split(/\s+/)
          clientData.firstName = nameParts[0] || ''
          clientData.lastName = nameParts.slice(1).join(' ') || ''
        }
        if (prefillEmail) {
          clientData.email = prefillEmail
        }
        if (prefillPhone) {
          clientData.phone = prefillPhone
        }
        if (prefillAddress) {
          // Parse "street, city, state zip" format for Step 4 Job Address
          const parts = prefillAddress.split(', ')
          if (parts.length >= 3) {
            setJobAddress(parts[0])
            setJobCity(parts[1])
            const stateZip = parts[2].split(' ')
            setJobState(stateZip[0] || '')
            setJobZip(stateZip[1] || '')
          } else if (parts.length === 2) {
            setJobAddress(parts[0])
            setJobCity(parts[1])
          } else {
            setJobAddress(prefillAddress)
          }
        }
        setNewClient(clientData)
      } else {
        setShowNewClientForm(false)
        setNewClient({ firstName: '', lastName: '', email: '', phone: '', company: '', isResidence: false, address: '', city: '', state: '', zip: '' })
      }
    }
  }, [open, prefillClientName, prefillAddress, prefillEmail, prefillPhone])

  // Scroll new client form into view when it opens
  useEffect(() => {
    if (showNewClientForm && newClientFormRef.current) {
      setTimeout(() => {
        newClientFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 50)
    }
  }, [showNewClientForm])

  // Seed the editable address when a different client is selected. Guarded on
  // `addressLoadedFor` so a later `clients` update (e.g. inline creation pushes
  // into the list) can't stomp an edit the user is part-way through typing.
  useEffect(() => {
    if (!selectedClientId) {
      setClientAddress(EMPTY_CLIENT_ADDRESS)
      loadedClientAddressRef.current = EMPTY_CLIENT_ADDRESS
      setAddressLoadedFor('')
      return
    }
    if (selectedClientId === addressLoadedFor) return
    const client = clients.find((c) => c.id === selectedClientId)
    if (!client) return // client list still loading
    const loaded: ClientAddress = {
      address: client.address || '',
      city: client.city || '',
      state: client.state || '',
      zip: client.zip || '',
    }
    setClientAddress(loaded)
    loadedClientAddressRef.current = loaded
    setAddressLoadedFor(selectedClientId)
  }, [selectedClientId, clients, addressLoadedFor])

  // Handle transition end to clear animating flag
  useEffect(() => {
    const track = trackRef.current
    if (!track) return
    const handler = () => setIsAnimating(false)
    track.addEventListener('transitionend', handler)
    return () => track.removeEventListener('transitionend', handler)
  }, [])

  // Close client dropdown on outside click
  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (clientComboRef.current && !clientComboRef.current.contains(e.target as Node)) {
        setShowClientDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [])

  function openClientDropdown() {
    if (clientInputWrapRef.current) {
      const rect = clientInputWrapRef.current.getBoundingClientRect()
      setDropdownPos({ top: rect.bottom + 6, left: rect.left, width: rect.width })
    }
    setShowClientDropdown(true)
  }

  // Filtered client list for combobox
  const filteredClients = useMemo(() => {
    if (!clientSearch.trim()) return clients
    const q = clientSearch.toLowerCase()
    return clients.filter(
      (c) => c.name.toLowerCase().includes(q) || (c.company || '').toLowerCase().includes(q)
    )
  }, [clients, clientSearch])

  async function loadClients() {
    try {
      const data = await clientsService.list()
      setClients(data)
    } catch {
      setClients([])
    }
  }

  async function loadCompanyMarkup() {
    try {
      const settings = await companyService.getSettings()
      if (settings.defaultMarkupPercent !== undefined && settings.defaultMarkupPercent !== null) {
        setCompanyMarkup(settings.defaultMarkupPercent / 100) // Convert 15 → 0.15
      }
    } catch {
      // Fall back to Prisma default (15%)
    }
  }

  function advanceStep() {
    setDirection('forward')
    setIsAnimating(true)
    setStep((prev) => prev + 1)
    setTimeout(() => setIsAnimating(false), 400)
  }

  async function goNext() {
    if (step >= TOTAL_STEPS - 1 || !canContinue() || isAnimating) return
    // If on client step with new client form, create client then advance
    if (step === 2 && showNewClientForm && selectedClientId === '') {
      setClientError('')
      if (!newClient.firstName.trim() && !newClient.lastName.trim()) { setClientError('Customer name is required'); return }
      setIsCreatingClient(true)
      try {
        const created = await clientsService.create({
          firstName: newClient.firstName.trim() || undefined,
          lastName: newClient.lastName.trim() || undefined,
          email: newClient.email.trim() || undefined,
          phone: newClient.phone.trim() || undefined,
          company: newClient.company.trim() || undefined,
          isResidence: newClient.isResidence,
          address: newClient.address.trim() || undefined,
          city: newClient.city.trim() || undefined,
          state: newClient.state.trim() || undefined,
          zip: newClient.zip.trim() || undefined,
        })
        setClients((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)))
        setSelectedClientId(created.id)
        setShowNewClientForm(false)
        setNewClient({ firstName: '', lastName: '', email: '', phone: '', company: '', isResidence: false, address: '', city: '', state: '', zip: '' })
        advanceStep()
      } catch (err: unknown) {
        const errData = (err as { response?: { data?: { message?: string } } })?.response?.data
        const msg = errData?.message || (err instanceof Error ? err.message : 'Failed to create customer')
        setClientError(msg)
      } finally {
        setIsCreatingClient(false)
      }
      return
    }

    // Existing client selected: persist address edits before advancing. This
    // writes the shared Client record, so it applies to that customer's other
    // estimates too — hence only firing when something actually changed.
    if (step === 2 && selectedClientId) {
      const changes = diffClientAddress(loadedClientAddressRef.current, clientAddress)
      if (changes) {
        setClientError('')
        setIsSavingClientAddress(true)
        try {
          const updated = await clientsService.update(selectedClientId, changes)
          setClients((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))
          loadedClientAddressRef.current = { ...loadedClientAddressRef.current, ...changes }
        } catch (err: unknown) {
          const errData = (err as { response?: { data?: { message?: string } } })?.response?.data
          const msg = errData?.message || (err instanceof Error ? err.message : 'Failed to save the customer address')
          setClientError(msg)
          return
        } finally {
          setIsSavingClientAddress(false)
        }
      }
    }

    advanceStep()
  }

  function goBack() {
    if (step <= 0 || isAnimating) return
    setDirection('back')
    setIsAnimating(true)
    setStep(step - 1)
    setTimeout(() => setIsAnimating(false), 400)
  }

  void direction // used conceptually for future enhancements

  function toggleType(typeId: string) {
    const next = new Set(selectedTypes)
    if (next.has(typeId)) {
      next.delete(typeId)
    } else {
      next.add(typeId)
    }
    setSelectedTypes(next)
  }


  function canContinue(): boolean {
    switch (step) {
      case 0: return true
      case 1: return selectedTypes.size > 0
      case 2: {
        if (selectedClientId !== '') return true
        if (showNewClientForm) {
          return newClient.firstName.trim() !== '' || newClient.lastName.trim() !== ''
        }
        return false
      }
      case 3: return projectName.trim() !== '' && jobAddress.trim() !== '' && jobCity.trim() !== '' && jobState.trim() !== '' && jobZip.trim() !== '' && supervisorName.trim() !== ''
      default: return false
    }
  }

  async function handleStartEstimate() {
    if (!canContinue()) return
    setIsSubmitting(true)
    setSubmitError('')

    const selectedClient = clients.find((c) => c.id === selectedClientId)

    const payload: CreateEstimatePayload = {
      title: projectName.trim(),
      clientName: selectedClient?.name || '',
      clientEmail: selectedClient?.email || undefined,
      clientPhone: selectedClient?.phone || undefined,
      clientCompany: selectedClient?.company || undefined,
      jobAddress: jobAddress.trim(),
      jobCity: jobCity.trim(),
      jobState: jobState.trim(),
      jobZip: jobZip.trim(),
      isPublicWorks,
      isUnitPricing,
      projectTypes: JSON.stringify(Array.from(selectedTypes)),
      supervisorName: supervisorName.trim(),
      supervisorPhone: supervisorPhone.trim() || undefined,
      supervisorEmail: supervisorEmail.trim() || undefined,
      clientId: selectedClientId || undefined,
      defaultMarkup: companyMarkup ?? undefined,
      scopeOfWork: prefillScopeOfWork || undefined,
    }

    try {
      const estimate = await estimatesService.create(payload)
      onOpenChange(false)
      navigate(`/estimates/${estimate.id}`)
    } catch (err: unknown) {
      const errData = (err as { response?: { data?: { message?: string } } })?.response?.data
      const msg = errData?.message || (err instanceof Error ? err.message : 'Failed to create estimate')
      setSubmitError(msg)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!open) return null

  // const _selectedClient = clients.find((c) => c.id === selectedClientId)
  const isRfpPrefill = !!(prefillClientName || prefillAddress || prefillEmail || prefillPhone || prefillScopeOfWork)

  return (
    <div className="wz-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onOpenChange(false); }}>
      <div className="wz-dialog">
        {/* Header with dots */}
        <div className="wz-header">
          <div className="wz-dots">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <span key={i} style={{ display: 'contents' }}>
                <span className={`wz-dot ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`} />
                {i < TOTAL_STEPS - 1 && <span className={`wz-dot-line ${i < step ? 'done' : ''}`} />}
              </span>
            ))}
          </div>
          <button className="wz-close" onClick={() => onOpenChange(false)}>
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Carousel viewport */}
        <div className="wz-viewport">
          <div
            ref={trackRef}
            className="wz-track"
            style={{ transform: `translateX(-${step * 100}%)` }}
          >
            {/* Step 1: Public Works */}
            <div className="wz-slide">
              <div className="wz-slide-inner">
                <img src="/images/wz-step1.png" alt="" className="wz-step-icon" />
                <h2 className="wz-question">Is this a public works project?</h2>
                <p className="wz-hint">Public works projects may require prevailing wages and certified payroll.</p>
                <div className="wz-toggle-group">
                  <button
                    className={`wz-toggle-btn ${isPublicWorks ? 'active' : ''}`}
                    onClick={() => setIsPublicWorks(true)}
                  >
                    Yes
                  </button>
                  <button
                    className={`wz-toggle-btn ${!isPublicWorks ? 'active' : ''}`}
                    onClick={() => setIsPublicWorks(false)}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>

            {/* Step 2: Project Types */}
            <div className="wz-slide">
              <div className="wz-slide-inner">
                <img src="/images/wz-step2.png" alt="" className="wz-step-icon" />
                <h2 className="wz-question">Great, now what type of project is this?</h2>
                <p className="wz-hint">Select all that apply.</p>
                <div className="wz-pill-group">
                  {PROJECT_TYPES.map((pt) => (
                    <button
                      key={pt.id}
                      className={`wz-pill ${selectedTypes.has(pt.id) ? 'active' : ''}`}
                      onClick={() => toggleType(pt.id)}
                    >
                      {selectedTypes.has(pt.id) && (
                        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                          <path d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      {pt.label}
                    </button>
                  ))}
                </div>
                {selectedTypes.size === 0 && (
                  <p className="wz-error-text">Select at least one project type to continue.</p>
                )}
              </div>
            </div>

            {/* Step 3: Customer */}
            <div className="wz-slide">
              <div className="wz-slide-inner">
                <img src="/images/wz-step3.png" alt="" className="wz-step-icon" />
                <h2 className="wz-question">Who is the customer?</h2>
                {!isRfpPrefill && <p className="wz-hint">Select an existing customer or create a new one.</p>}

                {!showNewClientForm ? (
                  <>
                    {/* Searchable combobox */}
                    <div className="wz-combobox" ref={clientComboRef}>
                      <div className="wz-combobox-input-wrap" ref={clientInputWrapRef}>
                        <svg className="wz-combobox-icon" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
                        </svg>
                        <input
                          className="wz-combobox-input"
                          placeholder={selectedClientId ? (clients.find(c => c.id === selectedClientId)?.name ?? 'Search customers...') : 'Search customers...'}
                          value={selectedClientId && !showClientDropdown
                            ? (clients.find(c => c.id === selectedClientId)?.name ?? clientSearch)
                            : clientSearch}
                          onFocus={() => {
                            openClientDropdown()
                            if (selectedClientId) {
                              setClientSearch('')
                            }
                          }}
                          onChange={(e) => {
                            setClientSearch(e.target.value)
                            setSelectedClientId('')
                            openClientDropdown()
                          }}
                        />
                        {selectedClientId && !showClientDropdown && (
                          <button
                            className="wz-combobox-clear"
                            onClick={() => { setSelectedClientId(''); setClientSearch(''); openClientDropdown(); }}
                            title="Clear selection"
                          >
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                              <path d="M18 6L6 18M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                      {showClientDropdown && dropdownPos && createPortal(
                        <div className="wz-combobox-dropdown" style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}>
                          {filteredClients.length > 0 ? (
                            filteredClients.map((c) => (
                              <button
                                key={c.id}
                                className="wz-combobox-option"
                                onMouseDown={(e) => {
                                  e.preventDefault()
                                  setSelectedClientId(c.id)
                                  setClientSearch('')
                                  setShowClientDropdown(false)
                                }}
                              >
                                <span className="wz-combobox-option-name">{c.name}</span>
                                {c.company && <span className="wz-combobox-option-company">{c.company}</span>}
                              </button>
                            ))
                          ) : (
                            <div className="wz-combobox-empty">
                              {clientSearch ? `No customers matching "${clientSearch}"` : 'No customers yet'}
                            </div>
                          )}
                        </div>,
                        document.body
                      )}
                    </div>
                    {/* Selected customer's own address — edits save to the shared
                        Client record, so they apply to that customer's other
                        estimates too. Not the job site, which is step 4. */}
                    {selectedClientId && (
                      <div style={{ marginTop: 20 }}>
                        <div className="wz-form-group">
                          <label className="wz-label">Customer Address</label>
                          <input
                            className="wz-input"
                            value={clientAddress.address}
                            onChange={(e) => setClientAddress({ ...clientAddress, address: e.target.value })}
                            placeholder="Billing or mailing address"
                          />
                        </div>

                        <div className="wz-form-row wz-form-row-3">
                          <div className="wz-form-group">
                            <label className="wz-label">City</label>
                            <input
                              className="wz-input"
                              value={clientAddress.city}
                              onChange={(e) => setClientAddress({ ...clientAddress, city: e.target.value })}
                              placeholder="City"
                            />
                          </div>
                          <div className="wz-form-group">
                            <label className="wz-label">State</label>
                            <select
                              className="wz-select"
                              value={clientAddress.state}
                              onChange={(e) => setClientAddress({ ...clientAddress, state: e.target.value })}
                            >
                              <option value="">State</option>
                              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </div>
                          <div className="wz-form-group">
                            <label className="wz-label">Zip</label>
                            <input
                              className="wz-input"
                              value={clientAddress.zip}
                              onChange={(e) => setClientAddress({ ...clientAddress, zip: e.target.value })}
                              placeholder="Zip"
                            />
                          </div>
                        </div>

                        {clientError && <p className="wz-error-text">{clientError}</p>}
                      </div>
                    )}

                    <div style={{ textAlign: 'center', marginTop: 12 }}>
                      <button
                        className="wz-link-btn"
                        style={{ fontSize: 13, color: 'var(--blue)', fontWeight: 500 }}
                        onClick={() => {
                          setShowClientDropdown(false)
                          setShowNewClientForm(true)
                          if (clientSearch) {
                            const parts = clientSearch.trim().split(/\s+/)
                            setNewClient((prev) => ({ ...prev, firstName: parts[0] || '', lastName: parts.slice(1).join(' ') || '' }))
                          }
                        }}
                      >
                        + Add a New Customer
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="wz-new-client-form" ref={newClientFormRef}>
                    <button
                      className="wz-link-btn"
                      style={{ marginBottom: 12 }}
                      onClick={() => { setShowNewClientForm(false); setNewClient({ firstName: '', lastName: '', email: '', phone: '', company: '', isResidence: false, address: '', city: '', state: '', zip: '' }); setClientError('') }}
                    >
                      ← Back to search
                    </button>
                    <div className="wz-form-row">
                      <div className="wz-form-group">
                        <label className="wz-label">First Name *</label>
                        <input
                          className="wz-input"
                          value={newClient.firstName}
                          onChange={(e) => setNewClient({ ...newClient, firstName: e.target.value })}
                          placeholder="John"
                        />
                      </div>
                      <div className="wz-form-group">
                        <label className="wz-label">Last Name</label>
                        <input
                          className="wz-input"
                          value={newClient.lastName}
                          onChange={(e) => setNewClient({ ...newClient, lastName: e.target.value })}
                          placeholder="Smith"
                        />
                      </div>
                    </div>
                    <div className="wz-form-row">
                      <div className="wz-form-group">
                        <label className="wz-label">Company</label>
                        <input
                          className="wz-input"
                          value={newClient.company}
                          onChange={(e) => setNewClient({ ...newClient, company: e.target.value })}
                          placeholder="Company name"
                        />
                      </div>
                    </div>

                    <div className="wz-form-row">
                      <div className="wz-form-group">
                        <label className="wz-label">Email</label>
                        <input
                          className="wz-input"
                          type="email"
                          value={newClient.email}
                          onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                          placeholder="email@example.com"
                        />
                      </div>
                      <div className="wz-form-group">
                        <label className="wz-label">Phone</label>
                        <input
                          className="wz-input"
                          value={newClient.phone}
                          onChange={(e) => setNewClient({ ...newClient, phone: formatPhone(e.target.value) })}
                          placeholder="555-555-5555"
                        />
                      </div>
                    </div>

                    <div className="wz-checkbox-row">
                      <input
                        type="checkbox"
                        id="wz-residence"
                        checked={newClient.isResidence}
                        onChange={(e) => setNewClient({ ...newClient, isResidence: e.target.checked })}
                      />
                      <label htmlFor="wz-residence">This is a residence owner</label>
                    </div>

                    {/* Customer's own address — not the job site, which is step 4. */}
                    <div className="wz-form-group">
                      <label className="wz-label">Customer Address</label>
                      <input
                        className="wz-input"
                        value={newClient.address}
                        onChange={(e) => setNewClient({ ...newClient, address: e.target.value })}
                        placeholder="Billing or mailing address"
                      />
                    </div>

                    <div className="wz-form-row wz-form-row-3">
                      <div className="wz-form-group">
                        <label className="wz-label">City</label>
                        <input
                          className="wz-input"
                          value={newClient.city}
                          onChange={(e) => setNewClient({ ...newClient, city: e.target.value })}
                          placeholder="City"
                        />
                      </div>
                      <div className="wz-form-group">
                        <label className="wz-label">State</label>
                        <select
                          className="wz-select"
                          value={newClient.state}
                          onChange={(e) => setNewClient({ ...newClient, state: e.target.value })}
                        >
                          <option value="">State</option>
                          {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div className="wz-form-group">
                        <label className="wz-label">Zip</label>
                        <input
                          className="wz-input"
                          value={newClient.zip}
                          onChange={(e) => setNewClient({ ...newClient, zip: e.target.value })}
                          placeholder="Zip"
                        />
                      </div>
                    </div>

                    {clientError && <p className="wz-error-text">{clientError}</p>}
                  </div>
                )}
              </div>
            </div>

            {/* Step 4: Project Details */}
            <div className="wz-slide">
              <div className="wz-slide-inner wz-form-slide wz-step4-slide">
                <h2 className="wz-question">Lastly, let's get some project details.</h2>

                <div style={{ height: 25 }} />

                <div className="wz-checkbox-row" style={{ marginBottom: 20 }}>
                  <input
                    type="checkbox"
                    id="wz-unit-pricing"
                    checked={isUnitPricing}
                    onChange={(e) => setIsUnitPricing(e.target.checked)}
                  />
                  <label htmlFor="wz-unit-pricing">Unit Pricing Proposal</label>
                </div>

                <div className="wz-form-group">
                  <label className="wz-label">Project Name *</label>
                  <input
                    className="wz-input"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    placeholder="e.g. Westfield Parking Lot Repairs"
                  />
                </div>

                <div className="wz-form-group">
                  <label className="wz-label">Job Address *</label>
                  <AddressAutocomplete
                    value={jobAddress}
                    onChange={setJobAddress}
                    onSelect={(address, city, state, zip) => {
                      setJobAddress(address)
                      setJobCity(city)
                      setJobState(state)
                      setJobZip(zip)
                    }}
                    placeholder="Start typing an address..."
                    className="wz-input"
                  />
                </div>

                <div className="wz-form-row wz-form-row-3">
                  <div className="wz-form-group">
                    <label className="wz-label">City *</label>
                    <input
                      className="wz-input"
                      value={jobCity}
                      onChange={(e) => setJobCity(e.target.value)}
                      placeholder="City"
                    />
                  </div>
                  <div className="wz-form-group">
                    <label className="wz-label">State *</label>
                    <select
                      className="wz-select"
                      value={jobState}
                      onChange={(e) => setJobState(e.target.value)}
                    >
                      <option value="">State</option>
                      {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="wz-form-group">
                    <label className="wz-label">Zip *</label>
                    <input
                      className="wz-input"
                      value={jobZip}
                      onChange={(e) => setJobZip(e.target.value)}
                      placeholder="Zip"
                    />
                  </div>
                </div>

                <div className="wz-divider" />

                <div className="wz-form-group">
                  <label className="wz-label">Supervisor Name *</label>
                  <input
                    className="wz-input"
                    value={supervisorName}
                    onChange={(e) => setSupervisorName(e.target.value)}
                    placeholder="Supervisor name"
                  />
                </div>

                <div className="wz-form-row">
                  <div className="wz-form-group">
                    <label className="wz-label">Supervisor Phone</label>
                    <input
                      className="wz-input"
                      value={supervisorPhone}
                      onChange={(e) => setSupervisorPhone(formatPhone(e.target.value))}
                      placeholder="555-555-5555"
                    />
                  </div>
                  <div className="wz-form-group">
                    <label className="wz-label">Supervisor Email</label>
                    <input
                      className="wz-input"
                      type="email"
                      value={supervisorEmail}
                      onChange={(e) => setSupervisorEmail(e.target.value)}
                      placeholder="email@example.com"
                    />
                  </div>
                </div>

                {submitError && <p className="wz-error-text">{submitError}</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="wz-footer">
          <div>
            {step > 0 && (
              <button className="wz-btn-outline" onClick={goBack} disabled={isAnimating}>
                <svg className="wz-btn-icon" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M19 12H5m0 0l6 6m-6-6l6-6" /></svg>
                Back
              </button>
            )}
          </div>
          <div>
            {step < TOTAL_STEPS - 1 ? (
              <button
                className="wz-btn-primary"
                disabled={!canContinue() || isAnimating || isCreatingClient || isSavingClientAddress}
                onClick={goNext}
              >
                {isCreatingClient ? 'Creating Customer...' : isSavingClientAddress ? 'Saving Address...' : 'Continue'}
                {!isCreatingClient && !isSavingClientAddress && <svg className="wz-btn-icon" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 12h14m0 0l-6-6m6 6l-6 6" /></svg>}
              </button>
            ) : (
              <button
                className="wz-btn-primary wz-btn-start"
                disabled={!canContinue() || isSubmitting}
                onClick={handleStartEstimate}
              >
                {isSubmitting ? 'Creating...' : 'Start Estimate'}
                {!isSubmitting && <svg className="wz-btn-icon" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 12h14m0 0l-6-6m6 6l-6 6" /></svg>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
